import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API } from "../App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { TrendingUp, Building2, Download, Loader2, ShoppingBag, Award, Calendar, Table as TableIcon } from "lucide-react";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const SupervisorEarningsTab = () => {
  const [purchaseData, setPurchaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState('all');

  useEffect(() => {
    fetchPurchaseData();
  }, []);

  const fetchPurchaseData = async () => {
    setLoading(true);
    try {
      // Fetch earnings data (includes payment history and totals)
      const response = await axios.get(`${API}/supervisor/earnings`);
      setPurchaseData(response.data);
    } catch (error) {
      console.error("Failed to fetch purchase data:", error);
      toast.error("Failed to load earnings information");
    } finally {
      setLoading(false);
    }
  };

  // Filter purchases based on date range
  const filteredPurchases = useMemo(() => {
    if (!purchaseData || !purchaseData.payment_history || !Array.isArray(purchaseData.payment_history)) {
      return [];
    }

    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    
    return purchaseData.payment_history.filter(payment => {
      if (!payment.purchase_date) return true;
      
      const purchaseDate = new Date(payment.purchase_date);
      
      switch(dateFilter) {
        case 'thisMonth':
          return purchaseDate.getFullYear() === currentYear && 
                 purchaseDate.getMonth() === currentMonth;
        
        case 'lastMonth':
          const lastMonth = currentMonth === 0 ? 11 : currentMonth - 1;
          const lastMonthYear = currentMonth === 0 ? currentYear - 1 : currentYear;
          return purchaseDate.getFullYear() === lastMonthYear && 
                 purchaseDate.getMonth() === lastMonth;
        
        case 'yearToDate':
          const startOfYear = new Date(currentYear, 0, 1);
          return purchaseDate >= startOfYear && purchaseDate <= now;
        
        case 'lastYear':
          return purchaseDate.getFullYear() === currentYear - 1;
        
        case 'all':
        default:
          return true;
      }
    });
  }, [purchaseData, dateFilter]);

  // Calculate totals for filtered data
  const filteredTotals = useMemo(() => {
    if (filteredPurchases.length === 0) {
      return { totalHours: 0, totalAmount: 0, totalPayments: 0 };
    }
    
    return {
      totalHours: filteredPurchases.reduce((sum, p) => sum + (p.hours_sold || 0), 0),
      totalAmount: filteredPurchases.reduce((sum, p) => sum + (p.amount_earned || 0), 0),
      totalPayments: filteredPurchases.length
    };
  }, [filteredPurchases]);

  const exportToCSV = () => {
    if (filteredPurchases.length === 0) {
      toast.error('No data to export');
      return;
    }

    const headers = ['Company Name', 'Hours Sold', 'Amount Earned (€)', 'Purchase Date', 'Status', 'Currency'];
    const rows = filteredPurchases.map(payment => [
      payment.company_name || 'Unknown Company',
      payment.hours_sold,
      payment.amount_earned.toFixed(2),
      payment.purchase_date ? new Date(payment.purchase_date).toLocaleDateString() : 'N/A',
      payment.paid ? 'Paid' : 'Pending',
      payment.currency || 'EUR'
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `club_earnings_${dateFilter}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('CSV exported successfully');
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-gold-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Club Earnings</h2>
          <p className="text-gray-600">Track your club's revenue from volunteer hour sales</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
          <Select value={dateFilter} onValueChange={setDateFilter}>
            <SelectTrigger className="w-full sm:w-[200px]">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Select period" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Time</SelectItem>
              <SelectItem value="thisMonth">This Month</SelectItem>
              <SelectItem value="lastMonth">Last Month</SelectItem>
              <SelectItem value="yearToDate">Year to Date</SelectItem>
              <SelectItem value="lastYear">Last Year</SelectItem>
            </SelectContent>
          </Select>
          {filteredPurchases.length > 0 && (
            <Button
              onClick={exportToCSV}
              className="!bg-green-600 hover:!bg-green-700 !text-white"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          )}
        </div>
      </div>

      {/* Summary Stats - Filtered */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Earned</p>
                <p className="text-3xl font-bold text-green-700">
                  €{filteredTotals.totalAmount.toFixed(2)}
                </p>
                {dateFilter !== 'all' && (
                  <p className="text-xs text-gray-500 mt-1">
                    {dateFilter === 'thisMonth' && 'This Month'}
                    {dateFilter === 'lastMonth' && 'Last Month'}
                    {dateFilter === 'yearToDate' && 'Year to Date'}
                    {dateFilter === 'lastYear' && 'Last Year'}
                  </p>
                )}
              </div>
              <TrendingUp className="w-8 h-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Hours Sold</p>
                <p className="text-3xl font-bold text-purple-700">
                  {filteredTotals.totalHours}
                </p>
                {dateFilter !== 'all' && (
                  <p className="text-xs text-gray-500 mt-1">Filtered view</p>
                )}
              </div>
              <Award className="w-8 h-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Payments</p>
                <p className="text-3xl font-bold text-blue-700">
                  {filteredTotals.totalPayments}
                </p>
                {dateFilter !== 'all' && (
                  <p className="text-xs text-gray-500 mt-1">Filtered view</p>
                )}
              </div>
              <Building2 className="w-8 h-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Purchase Details Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <TableIcon className="w-5 h-5 text-green-600" />
            <CardTitle>Purchase Details</CardTitle>
          </div>
          <CardDescription>
            Detailed breakdown of all purchases ({filteredPurchases.length} {filteredPurchases.length === 1 ? 'purchase' : 'purchases'})
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredPurchases.length === 0 ? (
            <div className="text-center py-12">
              <ShoppingBag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No purchases found</p>
              <p className="text-sm text-gray-400 mt-2">
                {dateFilter !== 'all' 
                  ? 'Try selecting a different date range'
                  : 'Your club\'s volunteer hours will appear here once they are purchased by companies'}
              </p>
            </div>
          ) : (
            <>
              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-gray-200">
                      <th className="text-left p-3 text-sm font-semibold text-gray-700">Company Name</th>
                      <th className="text-right p-3 text-sm font-semibold text-gray-700">Hours</th>
                      <th className="text-right p-3 text-sm font-semibold text-gray-700">Amount</th>
                      <th className="text-left p-3 text-sm font-semibold text-gray-700">Purchase Date</th>
                      <th className="text-center p-3 text-sm font-semibold text-gray-700">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPurchases.map((payment, idx) => (
                      <tr 
                        key={idx} 
                        className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                      >
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-green-600" />
                            <span className="font-medium text-gray-900">
                              {payment.company_name || 'Unknown Company'}
                            </span>
                          </div>
                        </td>
                        <td className="p-3 text-right">
                          <span className="font-semibold text-gray-900">{payment.hours_sold}h</span>
                        </td>
                        <td className="p-3 text-right">
                          <span className="font-bold text-green-700">
                            €{payment.amount_earned.toFixed(2)}
                          </span>
                        </td>
                        <td className="p-3">
                          <span className="text-gray-700">
                            {payment.purchase_date 
                              ? new Date(payment.purchase_date).toLocaleDateString('en-GB', {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric'
                                })
                              : 'N/A'}
                          </span>
                        </td>
                        <td className="p-3 text-center">
                          <Badge 
                            className={payment.paid 
                              ? "!bg-green-100 !text-green-800" 
                              : "!bg-yellow-100 !text-yellow-800"
                            }
                          >
                            {payment.paid ? 'Paid' : 'Pending'}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {/* Totals Row */}
                  <tfoot>
                    <tr className="border-t-2 border-gray-300 bg-gray-50 font-bold">
                      <td className="p-3 text-gray-900">TOTAL</td>
                      <td className="p-3 text-right text-gray-900">{filteredTotals.totalHours}h</td>
                      <td className="p-3 text-right text-green-700">€{filteredTotals.totalAmount.toFixed(2)}</td>
                      <td className="p-3 text-gray-900">{filteredTotals.totalPayments} {filteredTotals.totalPayments === 1 ? 'purchase' : 'purchases'}</td>
                      <td className="p-3"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Info Box */}
      {purchaseData && purchaseData.payment_history && purchaseData.payment_history.length > 0 && (
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4">
            <p className="text-sm text-green-900">
              <strong>ℹ️ Note:</strong> The figures quoted above are net figures transferred to the club's account. 
              These amounts represent your club's 90% share after the 10% platform fee has been deducted.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SupervisorEarningsTab;
